import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { OverlayPanel } from 'primereact/overlaypanel';
import { NotificacoesService, HistoricosService } from '../../../../services';
import type { HistoricoItem } from '../../../../services/notificacoes';
import Agro1Toast from '../../../../components/Agro1Toast';
import NotificacoesSkeleton from './notificacoes-skeleton';
import NotificacaoRow from './notificacao-row';

export interface PropriedadeNotificacoesRef {
  toggle: (
    target: HTMLElement,
    propriedadeId: number,
    propriedadeNome: string,
  ) => void;
}

interface PropriedadeNotificacoesProps {
  onRead?: (propriedadeId: number) => void;
}

const PropriedadeNotificacoes = forwardRef<
  PropriedadeNotificacoesRef,
  PropriedadeNotificacoesProps
>(({ onRead }, ref) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const propriedadeIdRef = useRef<number | null>(null);
  const isVisibleRef = useRef(false);
  const [nome, setNome] = useState('');
  const [propriedadeId, setPropriedadeId] = useState(0);
  const [items, setItems] = useState<HistoricoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const openFor = useImmutableCallback(
    (target: HTMLElement, id: number, propriedadeNome: string) => {
      targetRef.current = target;
      propriedadeIdRef.current = id;
      setNome(propriedadeNome);
      setPropriedadeId(id);
      setItems([]);
      setLoading(true);
      setError(false);
      onRead?.(id);
      HistoricosService.markAsRead(id).catch(console.error);
      overlayRef.current?.show({ currentTarget: target } as never, target);
    },
  );

  const toggle = useImmutableCallback(
    (target: HTMLElement, id: number, propriedadeNome: string) => {
      if (isVisibleRef.current) {
        overlayRef.current?.hide();
        if (propriedadeIdRef.current !== id) {
          requestAnimationFrame(() => openFor(target, id, propriedadeNome));
        }
      } else {
        openFor(target, id, propriedadeNome);
      }
    },
  );

  useImperativeHandle(ref, () => ({ toggle }));

  const handleShowOverlay = useImmutableCallback(() => {
    isVisibleRef.current = true;
    const overlayElement = overlayRef.current?.getElement();
    if (overlayElement && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      overlayElement.style.top = `${rect.bottom + window.scrollY}px`;
    }
    handleShow();
  });

  const handleHideOverlay = useImmutableCallback(() => {
    isVisibleRef.current = false;
  });

  const handleShow = useImmutableCallback(() => {
    if (propriedadeIdRef.current != null) {
      NotificacoesService.byPropriedade(propriedadeIdRef.current)
        .then(setItems)
        .catch((err) => {
          console.error('[notificacoes] falha ao carregar:', err);
          setError(true);
          Agro1Toast.show({
            detail: 'Erro ao carregar notificações. Tente novamente.',
            severity: 'warn',
          });
        })
        .finally(() => setLoading(false));
    }
  });

  return (
    <OverlayPanel
      ref={overlayRef}
      className="propriedade-notificacoes-overlay"
      dismissable
      appendTo={document.body}
      transitionOptions={{ classNames: 'notificacoes-panel', timeout: 180 }}
      onShow={handleShowOverlay}
      onHide={handleHideOverlay}
    >
      <div className="propriedade-notificacoes-content">
        <h3 className="notificacoes-titulo">
          Notificações {nome && `· ${nome}`}
        </h3>
        {loading && <NotificacoesSkeleton />}
        {!loading && error && (
          <p className="notificacoes-mensagem notificacoes-error">
            Erro ao carregar notificações. Tente novamente.
          </p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="notificacoes-mensagem">Nenhuma notificação.</p>
        )}
        {!loading && !error && items.length > 0 && (
          <table className="notificacoes-lista">
            <tbody>
              {items.map((item) => (
                <NotificacaoRow
                  key={item.id}
                  item={item}
                  propriedadeId={propriedadeId}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </OverlayPanel>
  );
});

PropriedadeNotificacoes.displayName = 'PropriedadeNotificacoes';

export default PropriedadeNotificacoes;
